import { AttachInternals, Component, Prop, State, Event, EventEmitter, Watch, h, Host } from '@stencil/core';
import {
  CONTROL_SUPPORTING_TEXT_VARIANT,
  CONTROL_TEXT_VARIANT,
  DEFAULT_REQUIRED_MESSAGE,
  setFormControlValue,
  setRequiredValidity,
} from '../../utils';

export type CheckboxSize = 'lg' | 'md' | 'sm' | 'xs';

let idCounter = 0;

@Component({
  tag: 'ds-checkbox',
  styleUrl: 'Checkbox.css',
  scoped: true,
  formAssociated: true,
})
export class Checkbox {
  @AttachInternals() internals!: ElementInternals;
  private labelId = `ds-checkbox-label-${++idCounter}`;
  private descriptionId = `${this.labelId}-description`;

  /** Visible label and accessible name. Omitted only in presentation mode. */
  @Prop() label!: string;
  /** Optional supporting description associated with the checkbox. */
  @Prop() description?: string;
  /** Current checked state. */
  @Prop({ mutable: true }) checked: boolean = false;
  /** Visual and placement density. */
  @Prop() size: CheckboxSize = 'md';
  /** Native form field name. */
  @Prop({ reflect: true }) name: string | undefined;
  /** Submitted value when checked. */
  @Prop() value: string = 'on';
  /** Native disabled state. */
  @Prop({ reflect: true }) disabled: boolean = false;
  /** Require the checkbox to be checked for form validity. */
  @Prop({ reflect: true }) required: boolean = false;
  /** Validation message used when a required checkbox is unchecked. */
  @Prop() requiredMessage: string = DEFAULT_REQUIRED_MESSAGE;
  /** Mixed visual state. Activation clears it before toggling checked. */
  @Prop({ mutable: true }) indeterminate: boolean = false;
  /** Design-system inactive state. */
  @Prop() isInactive: boolean = false;
  /** Visual-only indicator for a composite control that owns selection semantics. */
  @Prop() presentation: boolean = false;

  /** Emitted after user activation with the new checked state. */
  @Event() dsChange!: EventEmitter<boolean>;

  private initialChecked = false;
  private initialIndeterminate = false;
  @State() private formDisabled = false;

  componentWillLoad() {
    this.initialChecked = this.checked;
    this.initialIndeterminate = this.indeterminate;
    this.syncFormValue();
  }

  @Watch('checked')
  @Watch('value')
  @Watch('disabled')
  @Watch('isInactive')
  @Watch('required')
  @Watch('presentation')
  syncFormValue() {
    const inactive = this.isInactive || this.disabled || this.formDisabled || this.presentation;
    setFormControlValue(this.internals, this.checked ? this.value : null, { inactive });
    const missing = this.required && !inactive && !this.checked;
    setRequiredValidity(this.internals, missing, this.requiredMessage);
  }

  formDisabledCallback(disabled: boolean) {
    this.formDisabled = disabled;
    this.syncFormValue();
  }

  formResetCallback() {
    this.checked = this.initialChecked;
    this.indeterminate = this.initialIndeterminate;
  }

  private handleActivate = () => {
    if (this.isInactive || this.disabled || this.formDisabled || this.presentation) return;
    if (this.indeterminate) this.indeterminate = false;
    this.checked = !this.checked;
    this.dsChange.emit(this.checked);
  };

  private handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.handleActivate();
    }
  };

  render() {
    const inactive = this.isInactive || this.disabled || this.formDisabled;
    const isMarked = this.checked || this.indeterminate;
    const invalid = this.required && !inactive && !this.checked;

    return (
      <Host
        role={this.presentation ? undefined : 'checkbox'}
        aria-checked={this.presentation ? undefined : this.indeterminate ? 'mixed' : String(this.checked)}
        aria-disabled={!this.presentation && inactive ? 'true' : undefined}
        aria-required={!this.presentation && this.required ? 'true' : undefined}
        aria-invalid={!this.presentation && invalid ? 'true' : undefined}
        aria-labelledby={this.presentation ? undefined : this.labelId}
        aria-describedby={
          !this.presentation && this.description ? this.descriptionId : undefined
        }
        aria-hidden={this.presentation ? 'true' : undefined}
        tabIndex={this.presentation || inactive ? -1 : 0}
        class={{
          checkbox: true,
          'checkbox--presentation': this.presentation,
          'checkbox--described': Boolean(this.description),
          [`checkbox--${this.size}`]: true,
          [`ds-control--${this.size}`]: true,
          'ds-control-inactive': inactive && !this.presentation,
          'ds-focus-ring-inset': !this.presentation,
          'ds-interaction-fill': !inactive && !this.presentation,
        }}
        onClick={this.presentation ? undefined : this.handleActivate}
        onKeyDown={this.presentation ? undefined : this.handleKeyDown}
      >
        <span class="checkbox__placement ds-interaction-fill__content" aria-hidden="true">
          <span class={{ box: true, 'box--marked': isMarked }}>
            {isMarked && (
              // eslint-disable-next-line local/prefer-ds-icon -- Checkbox owns this fixed state-mark geometry.
              <svg
                class="checkbox__mark"
                viewBox="0 0 16 16"
                fill="currentColor"
                focusable="false"
              >
                <path
                  d={
                    this.indeterminate
                      ? 'M12 7.375V8.625H4V7.375H12Z'
                      : 'M12.9756 4.65527L7.22559 11.4053C7.11258 11.5379 6.94946 11.6169 6.77539 11.624C6.60111 11.631 6.43095 11.5657 6.30762 11.4424L3.05762 8.19238L3.94238 7.30762L6.71289 10.0781L12.0244 3.84473L12.9756 4.65527Z'
                  }
                />
              </svg>
            )}
          </span>
        </span>
        {!this.presentation && (
          <span class="checkbox__copy ds-interaction-fill__content">
            <ds-text
              class="checkbox__label"
              as="span"
              variant={CONTROL_TEXT_VARIANT[this.size]}
              textId={this.labelId}
            >
              {this.label}
            </ds-text>
            {this.description ? (
              <ds-text
                as="span"
                variant={CONTROL_SUPPORTING_TEXT_VARIANT[this.size]}
                color="secondary"
                textId={this.descriptionId}
              >
                {this.description}
              </ds-text>
            ) : null}
          </span>
        )}
      </Host>
    );
  }
}
